import React, { useMemo, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box, FormControl, FormHelperText } from '@mui/material';

interface Props {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: boolean;
  minHeight?: number;
  required?: boolean;
}

const QuillTextField: React.FC<Props> = ({
  label,
  value,
  onChange,
  error = false,
  minHeight = 120,
  required = false,
}) => {
  const [focused, setFocused] = useState(false);

  const isActive = useMemo(() => {
    const text = value
      .replace(/<(.|\n)*?>/g, '')
      .replace(/\uFEFF/g, '')
      .trim();
    return focused || text.length > 0;
  }, [focused, value]);

  return (
    <FormControl fullWidth margin="normal" error={error} required={required}>
      <Box
        sx={{
          position: 'relative',
          border: 1,
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 1,
          px: 1,
          pt: 1.5,
          // label container height space
          '& .ql-toolbar': {
            border: 'none',
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: '6px 6px 0 0',
            // match MUI TextField font
            fontFamily: theme => theme.typography.fontFamily,
            fontSize: theme => `${theme.typography.fontSize}px`,
          },
          '& .ql-container': {
            border: 'none',
            minHeight,
            borderRadius: '0 0 6px 6px',
            backgroundColor: theme => theme.palette.background.paper,
            // give inner editor padding so label doesn't overlap text
            paddingTop: '4px',
            // ensure editor content uses MUI typography
            '& .ql-editor': {
              fontFamily: theme => theme.typography.fontFamily,
              fontSize: theme => `${theme.typography.fontSize}px`,
              color: theme => theme.palette.text.primary,
              padding: '8px 12px',
              minHeight: `calc(${minHeight}px - 8px)`,
            },
            '& .ql-editor *': {
              fontFamily: theme => theme.typography.fontFamily,
            },
          },
        }}
      >
        <Box
          component="span"
          sx={theme => ({
            position: 'absolute',
            left: 12,
            // match MUI TextField float behaviour
            top: isActive ? -9 : 18,
            transform: isActive
              ? 'scale(0.75) translateY(0)'
              : 'scale(1) translateY(0)',
            transformOrigin: 'left top',
            transition: 'all 150ms cubic-bezier(.4,0,.2,1)',
            backgroundColor: theme.palette.background.paper,
            px: 0.5,
            // use same sizing as MUI: 16px when resting, slightly smaller when floated
            fontSize: isActive ? 14 : 16,
            lineHeight: 1,
            // color like MUI
            color: error
              ? theme.palette.error.main
              : theme.palette.text.secondary,
            pointerEvents: 'none',
          })}
        >
          <>
            {label}
            {required && (
              <Box
                component="span"
                sx={theme => ({
                  color: error
                    ? theme.palette.error.main
                    : theme.palette.text.secondary,
                  ml: 0.5,
                })}
                aria-hidden
              >
                *
              </Box>
            )}
          </>{' '}
        </Box>

        <ReactQuill
          theme="snow"
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          modules={{
            toolbar: [
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
            ],
          }}
          style={{ minHeight, background: 'transparent' }}
        />
      </Box>

      {error && (
        <FormHelperText>{`Please enter ${label.toLowerCase()}.`}</FormHelperText>
      )}
    </FormControl>
  );
};

export default QuillTextField;
